<template>
    <RPFilter
        ref="button"
        class="RPDateFilter"
        dropdown-position="left"
        @button-state="toggleButton"
    >
        <template v-slot>
            <div class="RPDateFilter__tag">
                <StreamlineIcon
                    class="RPDateFilter__tag-icon"
                    :icon="CalendarIcon"
                    :size="12"
                    :stroke="iconColor"
                />
                <span :style="buttonTextColor">{{ buttonText }}</span>
            </div>
        </template>

        <template v-slot:dropdown="{isModal}">
            <DateFilterDropdown
                :current-dates="value"
                :options="options"
                :is-modal="isModal"
                @close="closeDropdown"
                @select="select"
            />
        </template>
    </RPFilter>
</template>
<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {Calendar as CalendarIcon} from "@reviewpush/streamline-regular/lib/interface-essential/DateCalendar";
import colors from 'style/_colors.scss';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import RPFilter from '../RPFilter.vue';
import DateFilterDropdown from './_date-filter-dropdown.vue';

export default {
    name: 'RPDateFilter',
    components: {
        StreamlineIcon,
        RPFilter,
        DateFilterDropdown
    },
    props: {
        /**
         * @value
         */
        value: {
            type: Object,
            required: true,
        },
        preselectOptions: {
            type: Array,
            default: () => ([])
        }
    },
    data(){
        return {
            buttonOpen: false,
            CalendarIcon: CalendarIcon,
            grey: colors.grey,
            primary: colors.primary,
        };
    },
    computed: {
        buttonText(){
            let defaultText = 'Select Date';
            if(!isEmpty(this.value)){
                const preselect = this.options.find(opt => isEqual(opt.value, this.value));
                if(preselect) {
                    defaultText = preselect.label;
                } else {
                    defaultText = this.value.date_from === this.value.date_to
                        ? moment(this.value.date_from).format("D MMM, YYYY")
                        : `${moment(this.value.date_from).format("D MMM, YYYY")}-${moment(this.value.date_to).format("D MMM, YYYY")}`;
                }
            }
            return defaultText;
        },
        iconColor(){
            return this.buttonOpen
                ? this.primary
                : this.grey;
        },
        buttonTextColor(){
            return {
                color: this.buttonOpen
                    ? this.primary
                    : this.grey
            };
        },
        options(){
            let date = [
                {
                    label: 'Specific Date',
                    value: 'open-single'
                }
            ];

            date =  date.concat(this.preselectOptions.length
                ? this.preselectOptions
                : [
                    {
                        label: 'Today',
                        value: {
                            date_from: moment().subtract(1, 'days').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Yesterday',
                        value: {
                            date_from: moment().subtract(1, 'days').format('YYYY-MM-DD'),
                            date_to: moment().subtract(1, 'days').format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last 7 Days',
                        value: {
                            date_from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last 30 Days',
                        value: {
                            date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last 60 Days',
                        value: {
                            date_from: moment().subtract(60, 'days').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last 90 Days',
                        value: {
                            date_from: moment().subtract(90, 'days').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last 6 Months',
                        value: {
                            date_from: moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                    {
                        label: 'Last Year',
                        value: {
                            date_from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
                            date_to: moment().format('YYYY-MM-DD'),
                        }
                    },
                ]
            );

            date = date.concat([{
                label: 'Custom Range',
                value: 'open-double',
            }]);

            return date;
        }
    },
    methods: {
        toggleButton(e){
            this.buttonOpen = e;
        },
        select(s){
            /**
             * Used for `v-model`
             */
            this.$emit('input', s);
            this.closeDropdown();
        },
        closeDropdown(){
            this.$refs.button.close();
        }
    }
};
</script>
<style lang="scss">
    .RPDateFilter {
        background: $white;
        &__tag {
            display: flex;
            align-items: center;
        }
        &__tag-icon {
            margin-right: .5rem;
        }
    }
</style>

<docs>
```vue
import moment from 'moment';

<RPDateFilter
    v-model="myValObject"
>
</RPDateFilter>
<pre>{{ {"v-model": myValObject} }}</pre>

<p>Replace the preselected date ranges with your own:</p>
<RPDateFilter
    v-model="myValObject"
    :preselect-options="[
        {
            label: 'Last 2 Weeks',
            value: {
                date_from: moment().subtract(14, 'days').format('YYYY-MM-DD'),
                date_to: moment().format('YYYY-MM-DD'),
            }
        },
    ]"
>
</RPDateFilter>
```
</docs>
